import React from 'react';
import { Thermometer, HomeIcon, Wind, Droplets, Zap, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета теплопотерь
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  return (
    <ToolResult title="Результаты расчета теплопотерь">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Общие теплопотери */}
        <div className="rounded-lg bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/30 dark:to-orange-900/30 p-5 border border-red-100 dark:border-red-800/50 shadow-sm">
          <h3 className="text-base font-medium text-red-700 dark:text-red-300 mb-1.5 flex items-center gap-1.5">
            <Thermometer className="w-4 h-4"/>
            Общие теплопотери здания
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-red-800 dark:text-red-200" aria-live="polite">
              {formatNumber(results.totalHeatLoss, 0)} Вт
            </span>
            <span className="text-sm font-medium text-red-600 dark:text-red-400">
              ({formatNumber(results.specificHeatLoss, 1)} Вт/м²)
            </span>
          </div>
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
            При температуре наружного воздуха {results.outdoorTemperature}°C
          </p>
        </div>

        {/* Рекомендуемая мощность отопления */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Zap className="w-4 h-4"/>
            Рекомендуемая мощность отопления
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {formatNumber(results.recommendedHeatingCapacity, 1)} кВт
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            С запасом {formatNumber(results.heatingSafetyFactor * 100 - 100, 0)}% для устойчивого обогрева
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Удельный расход тепловой энергии" 
          value={`${formatNumber(results.specificEnergyConsumption, 1)} кВт·ч/м²·год`} 
          color="orange" 
          icon={<Zap className="w-4 h-4"/>}
          tooltip="Показатель энергоэффективности здания"
        />
        
        <ResultItem 
          label="Оценка энергоэффективности" 
          value={results.energyEfficiencyRating} 
          color={getEnergyRatingColor(results.energyEfficiencyRating)} 
          icon={<HomeIcon className="w-4 h-4"/>}
          tooltip="Класс энергоэффективности здания по российским нормам"
        />
        
        <ResultItem 
          label="Разница температур" 
          value={`${formatNumber(results.temperatureDifference, 1)}°C`} 
          color="red" 
          icon={<Thermometer className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Годовое потребление энергии (расчетное)" 
          value={`${formatNumber(results.annualEnergyConsumption, 0)} кВт·ч/год`} 
          color="blue" 
          icon={<Zap className="w-4 h-4"/>}
        />
        
        <div className="p-4 border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20 rounded-r-lg">
          <h3 className="text-base font-semibold text-red-700 dark:text-red-300 mb-2">Теплопотери по элементам здания:</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div className="flex items-center justify-between text-sm text-red-600 dark:text-red-400">
              <span>Через стены:</span>
              <span className="font-medium">{formatNumber(results.heatLosses.walls, 0)} Вт ({formatNumber(results.heatLossPercentages.walls, 1)}%)</span>
            </div>
            <div className="flex items-center justify-between text-sm text-red-600 dark:text-red-400">
              <span>Через окна:</span>
              <span className="font-medium">{formatNumber(results.heatLosses.windows, 0)} Вт ({formatNumber(results.heatLossPercentages.windows, 1)}%)</span>
            </div>
            <div className="flex items-center justify-between text-sm text-red-600 dark:text-red-400">
              <span>Через {results.roofDescription}:</span>
              <span className="font-medium">{formatNumber(results.heatLosses.roof, 0)} Вт ({formatNumber(results.heatLossPercentages.roof, 1)}%)</span>
            </div>
            <div className="flex items-center justify-between text-sm text-red-600 dark:text-red-400">
              <span>Через {results.floorDescription}:</span>
              <span className="font-medium">{formatNumber(results.heatLosses.floor, 0)} Вт ({formatNumber(results.heatLossPercentages.floor, 1)}%)</span>
            </div>
            <div className="flex items-center justify-between text-sm text-red-600 dark:text-red-400 col-span-full border-t border-red-200 dark:border-red-800 pt-2 mt-1">
              <span>Вентиляция и инфильтрация:</span>
              <span className="font-medium">{formatNumber(results.heatLosses.ventilation, 0)} Вт ({formatNumber(results.heatLossPercentages.ventilation, 1)}%)</span>
            </div>
          </div>
        </div>
      </div>

      {/* Информация о коэффициентах теплопроводности */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
          <Droplets className="w-4 h-4 text-blue-500"/>
          Коэффициенты теплопередачи (U-value):
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-col">
            <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
              <span>Стены:</span>
              <span className="font-medium">{formatNumber(results.uValues.walls, 3)} Вт/(м²·°C)</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {getUValueRating(results.uValues.walls, 'wall')}
            </div>
          </div>
          
          <div className="flex flex-col">
            <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
              <span>Окна:</span>
              <span className="font-medium">{formatNumber(results.uValues.windows, 3)} Вт/(м²·°C)</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {getUValueRating(results.uValues.windows, 'window')}
            </div>
          </div>
          
          <div className="flex flex-col">
            <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
              <span>{results.roofDescription}:</span>
              <span className="font-medium">{formatNumber(results.uValues.roof, 3)} Вт/(м²·°C)</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {getUValueRating(results.uValues.roof, 'roof')}
            </div>
          </div>
          
          <div className="flex flex-col">
            <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
              <span>{results.floorDescription}:</span>
              <span className="font-medium">{formatNumber(results.uValues.floor, 3)} Вт/(м²·°C)</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {getUValueRating(results.uValues.floor, 'floor')}
            </div>
          </div>
        </div>
      </div>

      {/* Рекомендации по энергоэффективности */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="flex items-start gap-3">
          <div className="mt-0.5">
            <AlertCircle className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">Рекомендации по улучшению энергоэффективности</h4>
            <div className="text-sm text-blue-600 dark:text-blue-400">
              {results.recommendations && results.recommendations.length > 0 ? (
                <ul className="list-disc pl-4 space-y-1">
                  {results.recommendations.map((rec, index) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              ) : (
                <p>Здание имеет хорошие показатели энергоэффективности. Для дальнейшего улучшения рассмотрите применение современных технологий отопления и вентиляции.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

// Вспомогательные функции для определения цветов и рейтингов

// Получение цвета для рейтинга энергоэффективности
function getEnergyRatingColor(rating) {
  switch (rating) {
    case 'А++':
    case 'А+':
    case 'А':
      return 'green';
    case 'B':
    case 'C':
      return 'teal';
    case 'D':
    case 'E':
      return 'orange';
    case 'F':
    case 'G':
      return 'red';
    default:
      return 'gray';
  }
}

// Оценка значения коэффициента теплопередачи (U-value)
function getUValueRating(uValue, type) {
  let rating;
  
  // Границы оценки зависят от типа конструкции
  switch (type) {
    case 'wall':
      if (uValue <= 0.15) rating = 'Отличная теплоизоляция (пассивный дом)';
      else if (uValue <= 0.3) rating = 'Хорошая теплоизоляция (современный стандарт)';
      else if (uValue <= 0.6) rating = 'Средняя теплоизоляция';
      else if (uValue <= 1.0) rating = 'Низкая теплоизоляция';
      else rating = 'Очень низкая теплоизоляция';
      break;
      
    case 'window':
      if (uValue <= 0.8) rating = 'Отличная теплоизоляция (пассивный дом)';
      else if (uValue <= 1.3) rating = 'Хорошая теплоизоляция (энергоэффективные окна)';
      else if (uValue <= 2.0) rating = 'Средняя теплоизоляция (современные окна)';
      else if (uValue <= 3.0) rating = 'Низкая теплоизоляция (стандартные окна)';
      else rating = 'Очень низкая теплоизоляция (устаревшие окна)';
      break;
      
    case 'roof':
      if (uValue <= 0.1) rating = 'Отличная теплоизоляция (пассивный дом)';
      else if (uValue <= 0.2) rating = 'Хорошая теплоизоляция (современный стандарт)';
      else if (uValue <= 0.3) rating = 'Средняя теплоизоляция';
      else if (uValue <= 0.5) rating = 'Низкая теплоизоляция';
      else rating = 'Очень низкая теплоизоляция';
      break;
      
    case 'floor':
      if (uValue <= 0.15) rating = 'Отличная теплоизоляция (пассивный дом)';
      else if (uValue <= 0.25) rating = 'Хорошая теплоизоляция (современный стандарт)';
      else if (uValue <= 0.4) rating = 'Средняя теплоизоляция';
      else if (uValue <= 0.6) rating = 'Низкая теплоизоляция';
      else rating = 'Очень низкая теплоизоляция';
      break;
      
    default:
      rating = 'Не определено';
  }
  
  return rating;
}

export default ResultsOverview;